import React, { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

type HeidiErrorType = {
  errorCount: number;
  isActualError: boolean;
  isPollingError: boolean;
  reportError: () => void;
  reportSuccess: () => void;
};

const HeidiErrorContext = createContext<HeidiErrorType | null>(null);

type HeidiErrorPropType = {
  children: ReactNode;
};

const useHeidiError = () => {
  const context = useContext(HeidiErrorContext);
  if (!context) {
    throw new Error('useHeidiError must be used within HeidiErrorProvider');
  }
  return context;
};

/* 
 The code below initially suppresses the first few errors as Heidi will
 not return a successful response if there is no transcribed text to return.
 This means after initialising a session and starting to poll, 502s will be 
 shown up until the first audio chunk has been translated to text.

 There's a chance that the response is successful but the transcript is still null. 
 Possibly because Heidi is still transcribing the first chunk.

 If we didn't do this then an error would immediately be shown misleading the 
 clinicians as it will eventually disappear when the first chunk has been transcribed.
*/

const NUMBER_SUPPRESSED_ERROR_CALLS = 3;
const NUMBER_RETRY_ERROR_CALLS = 5;

export const HeidiErrorProvider = ({ children }: HeidiErrorPropType) => {
  const [errorCount, setErrorCount] = useState(0);
  const [isActualError, setIsActualError] = useState(false);
  const [isPollingError, setIsPollingError] = useState(false);

  const reportSuccess = useCallback(() => {
    if (isActualError) return;

    if (errorCount > 0) {
      setIsPollingError(false);
      setErrorCount(0);
    }
  }, [isActualError, errorCount]);

  const reportError = useCallback(() => {
    setErrorCount((prev) => {
      const count = prev + 1;
      if (count > NUMBER_SUPPRESSED_ERROR_CALLS && count <= NUMBER_RETRY_ERROR_CALLS) {
        setIsPollingError(true);
      } else if (count > NUMBER_RETRY_ERROR_CALLS) {
        setIsPollingError(false);
        setIsActualError(true);
      }
      return count;
    });
  }, [NUMBER_RETRY_ERROR_CALLS, errorCount]);

  const value = useMemo(
    () => ({
      errorCount,
      isActualError,
      isPollingError,
      reportError,
      reportSuccess
    }),
    [isActualError, isPollingError, reportError, reportSuccess, errorCount]
  );

  return <HeidiErrorContext.Provider value={value}>{children}</HeidiErrorContext.Provider>;
};

export default useHeidiError;
